<template>
  <div
    v-if="isVisible"
    class="flex flex-col transition-all duration-300 ease-in-out"
    :class="{ 'pointer-events-none cursor-none opacity-40': isDisabled }">
    <div v-if="field.type === 'separator'">
      <Separator />
    </div>

    <div v-else-if="field.type === 'parent'" class="my-2 border-b border-gray-700 pb-1">
      <Label :text="field.label" :disabled="isDisabled" parent class="font-semibold text-gray-300!" />
    </div>

    <div v-else-if="field.type === 'color'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      <div class="align-center flex items-center justify-between space-x-3">
        <DColorPicker
          v-if="colorPickerVisible"
          v-model="modelValue"
          :disabled="isDisabled || overrideColorByGlobalMaterial"
          show-input
          fluid
          @contextmenu="showContextMenu" />
      </div>
    </div>

    <div v-else-if="field.type === 'select'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      <DSelect
        fluid
        v-model="modelValue"
        :options="field.options"
        optionLabel="label"
        optionValue="value"
        :disabled="isDisabled"
        @contextmenu="showContextMenu" />
    </div>

    <div v-else-if="field.type === 'asset'" class="flex flex-col gap-1">
      <div class="align-center flex items-center justify-between space-x-3">
        <Label :text="field.label" :inline="true" :disabled="isDisabled" parent />

        <AssetSelect v-model="modelValue" :disabled="isDisabled" type="asset" :filter="field.filter || 'media'" />
      </div>
    </div>

    <div v-else-if="field.type === 'library'" class="flex flex-col gap-1">
      <div class="align-center flex items-center justify-between space-x-3">
        <Label :text="field.label" :inline="true" :disabled="isDisabled" parent />

        <AssetSelect v-model="modelValue" :disabled="isDisabled" :type="field.libraryType" />
      </div>
    </div>

    <div v-else-if="field.type === 'boolean'" class="flex flex-col gap-1">
      <div class="align-center flex items-center justify-between space-x-3">
        <Label :text="field.label" :inline="true" :disabled="isDisabled" parent />
        <DToggleSwitch v-model="modelValue" :disabled="isDisabled" @contextmenu="showContextMenu" />
      </div>
    </div>

    <div v-else-if="field.type === 'radio'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      <DRadioGroup
        v-model="modelValue"
        :options="field.options"
        optionLabel="label"
        optionValue="value"
        :disabled="isDisabled"
        @contextmenu="showContextMenu" />
    </div>

    <div v-else-if="field.type === 'pattern-picker'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      <PatternPicker v-model="modelValue" :sources="field.sources" :disabled="isDisabled" />
    </div>

    <div v-else-if="field.type === 'text'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />
      <DInputText
        fluid
        v-model="modelValue"
        @contextmenu="showContextMenu"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="handleEnterKey"
        :disabled="isDisabled"
        :class="{
          'border-none bg-[#344054]!': isDisabled,
        }" />
    </div>

    <div v-else-if="field.type === 'angle'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      <div class="flex items-stretch">
        <DInputNumber
          class="w-full rounded-none border-gray-700"
          v-model="modelValue"
          :prepend="field.sublabel ? field.sublabel + ': ' : ''"
          :showButtons="false"
          fluid
          :step="measurementStep"
          :maxFractionDigits="measurementMaxFractionDigits"
          :disabled="isDisabled"
          buttonLayout="stacked"
          @contextmenu="showContextMenu"
          enable-drag
          enable-wheel
          :append="angleUnitLabel + ''"
          @focus="handleFocus"
          @blur="handleBlur"
          @valueChange="saveValue"
          @keydown.enter="handleEnterKey"
          size="small" />
      </div>
    </div>

    <div v-else-if="field.type === 'light' || field.type === 'ratio'" class="flex flex-col space-y-1">
      <Label :text="field.label" :disabled="isDisabled" parent />
      <DInputSlider
        :showButtons="false"
        type="number"
        :prepend="field.sublabel ? field.sublabel + ': ' : ''"
        v-model="modelValue"
        :step="field.step || 0.01"
        :min="field?.min"
        :max="field?.max"
        :maxFractionDigits="field.maxFractionDigits || 2"
        :disabled="isDisabled"
        @contextmenu="showContextMenu" />
    </div>

    <div v-else-if="field.type === 'measurement'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      <div class="flex w-full items-stretch">
        <DInputNumber
          class="w-full rounded-none border-gray-700"
          v-model="modelValue"
          :prepend="field.sublabel ? field.sublabel + ': ' : ''"
          :showButtons="false"
          fluid
          :step="measurementStep"
          :maxFractionDigits="measurementMaxFractionDigits"
          :disabled="isDisabled"
          buttonLayout="stacked"
          @contextmenu="showContextMenu"
          enable-drag
          enable-wheel
          :append="lengthUnitLabel + ''"
          @focus="handleFocus"
          @blur="handleBlur"
          @valueChange="saveValue"
          @keydown.enter="handleEnterKey"
          size="small" />
      </div>
    </div>

    <div v-else-if="field.type == 'slider'" class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />
      <DInputSlider
        v-model="modelValue"
        :min="field.min || 0"
        :max="field.max || 100"
        :step="field.step || 1"
        :disabled="isDisabled"
        @contextmenu="showContextMenu" />
    </div>

    <div v-else-if="field.type === 'number'" class="flex w-full flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />
      <div class="flex items-stretch">
        <DInputNumber
          class="w-full rounded-none border-gray-700"
          v-model="modelValue"
          :prepend="field.sublabel ? field.sublabel + ': ' : ''"
          :showButtons="false"
          :step="field.step"
          :maxFractionDigits="field.maxFractionDigits || 2"
          :min="field?.min"
          :max="field?.max"
          :enable-drag="true"
          :disabled="isDisabled"
          enable-drag
          buttonLayout="stacked"
          enable-wheel
          @contextmenu="showContextMenu"
          @focus="handleFocus"
          @blur="handleBlur"
          @valueChange="saveValue"
          @change="handleChange"
          @keydown.enter="handleEnterKey"
          size="small" />
      </div>
    </div>

    <template v-else-if="field.type === 'listuvs'"><ListUVs></ListUVs></template>

    <template v-else-if="field.type === 'projectorstats'">
      <ProjectorStats />
    </template>

    <template v-else-if="field.type === 'ledstats'">
      <LedStats />
    </template>

    <template v-else-if="field.type === 'photometricstats'">
      <PhotometricSpectrumTable />
    </template>

    <div v-else-if="field.type === 'button'">
      <DButton
        :label="field.buttonText || field.label"
        @click="handleButtonClick"
        :disabled="isDisabled"
        fluid
        severity="primary" />
    </div>

    <div v-else class="flex flex-col gap-1">
      <Label :text="field.label" :disabled="isDisabled" parent />

      {{ field.type }} unknown
    </div>
  </div>
</template>

<script setup>
import { useUnitConversion } from '@/composables/useUnitConversion';
import { SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { getObjectByCode } from '@/threejs/objectManager';
import {
  DButton,
  DColorPicker,
  DInputNumber,
  DInputSlider,
  DInputText,
  DRadioGroup,
  DSelect,
  DToggleSwitch,
} from '@disguise/cloud-ui';
import { computed, nextTick, ref, watch } from 'vue';
import AssetSelect from './panels/AssetSelect.vue';
import Label from './panels/Label.vue';
import LedStats from './panels/LedStats.vue';
import ListUVs from './panels/ListUVs.vue';
import PhotometricSpectrumTable from './panels/PhotometricSpectrumTable.vue';
import ProjectorStats from './panels/ProjectorStats.vue';
import Separator from './panels/Separator.vue';
import PatternPicker from './PatternPicker.vue';

const { getLengthUnitLabel, getAngleUnitLabel, convertLength, toMeters, lengthUnit } = useUnitConversion();

const props = defineProps({
  field: {
    type: Object,
    required: true,
  },
  value: {
    type: [String, Number, Boolean, Object, Array],
    default: null,
  },
  module: {
    type: Object,
    default: null,
  },
  disabled: {
    type: [Boolean],
    default: false,
  },
  resetCounter: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['change', 'contextmenu']);

const isEditing = ref(false);
const internalValue = ref(null);
const colorPickerVisible = ref(true);
const cruxModuleStore = useCruxModuleStore();

watch(
  () => props.value,
  (next) => {
    if (!isEditing.value) {
      internalValue.value = next !== undefined && next !== null ? next : (props.field.default ?? '');
    }
  },
  { immediate: true }
);

const isValidValue = (value, fieldType) => {
  const numericTypes = ['number', 'measurement', 'angle'];
  if (numericTypes.includes(fieldType)) {
    return value !== null && value !== '' && typeof value === 'number' && isFinite(value);
  }
  if (fieldType === 'text') {
    return typeof value === 'string' && value.trim() !== '';
  }
  return true;
};
watch(
  () => props.resetCounter,
  () => {
    if (props.field.type === 'color') {
      colorPickerVisible.value = false;
      nextTick(() => {
        colorPickerVisible.value = true;
      });
    }
  }
);

const modelValue = computed({
  get() {
    if (isEditing.value) {
      return internalValue.value;
    }
    let value = props.value !== undefined && props.value !== null ? props.value : (props.field.default ?? '');

    // Convert meters to display unit for measurement fields

    if (props.field.type === 'measurement' && typeof value === 'number') {
      return convertLength(value);
    }

    return value;
  },
  set(newValue) {
    if (newValue === '-' || newValue === null || newValue === '') {
      if (['number', 'measurement', 'angle'].includes(props.field.type)) {
        internalValue.value = newValue;
        return;
      }
    }
    // After blur, PrimeVue re-parses and emits update:modelValue.
    // For projector target fields the orbit session already committed the
    // correct value during change with phase='end' — suppress this late write to prevent
    // the fallback sphere-constraint from overwriting the orbit result.
    if (isProjectorTargetField.value && !isEditing.value) {
      return;
    }
    const object = props.module?.code ? getObjectByCode(props.module.code) : null;
    if (object && object.isTransforming) {
      return;
    }

    let clampedValue = newValue;
    let valueToEmit = newValue;
    if (typeof newValue === 'number') {
      const normalized = props.field.type === 'measurement' ? toMeters(newValue) : newValue;

      let bounded;
      if (props.field.type === 'angle') {
        bounded = ((normalized % 360) + 360) % 360;
      } else {
        const min = props.field.min;
        const max = props.field.max;
        bounded =
          max !== undefined && normalized > max ? max : min !== undefined && normalized < min ? min : normalized;
      }

      valueToEmit = bounded;
      clampedValue = props.field.type === 'measurement' ? convertLength(bounded) : bounded;
    }

    // Don't emit invalid values to store/renderer
    if (!isValidValue(valueToEmit, props.field.type)) {
      internalValue.value = newValue;
      return;
    }

    // Skip if value hasn't changed (prevents DInputNumber recursive loop)
    if (internalValue.value === clampedValue) return;

    internalValue.value = clampedValue;

    handleChange(valueToEmit);
  },
});

const handleFocus = () => {
  isEditing.value = true;
  // Capture the actual prop value, not the computed value with default

  let value = props.value !== undefined && props.value !== null ? props.value : (props.field.default ?? '');
  // Convert for measurement fields
  if (props.field.type === 'measurement' && typeof value === 'number') {
    value = convertLength(value);
  }
  internalValue.value = value;

  if (['number', 'measurement', 'angle'].includes(props.field.type)) {
    let lifecycleValue = value;
    if (props.field.type === 'measurement' && typeof lifecycleValue === 'number') {
      lifecycleValue = toMeters(lifecycleValue);
    }
    emit('change', lifecycleValue, false, 'start');
  }
};

const isProjectorTargetField = computed(() => {
  return (
    props.module?.type === 'projector' && typeof props.field?.key === 'string' && props.field.key.startsWith('target.')
  );
});

const handleBlur = () => {
  isEditing.value = false;
  let currentValue = internalValue.value;
  let endValue = currentValue;
  if (isValidValue(currentValue, props.field.type)) {
    let valueToEmit = currentValue;
    if (props.field.type === 'measurement' && typeof valueToEmit === 'number') {
      valueToEmit = toMeters(valueToEmit);
    }
    endValue = valueToEmit;

    // For non-numeric types, emit a regular change on blur
    if (!['number', 'measurement', 'angle'].includes(props.field.type)) {
      handleChange(valueToEmit);
    }
  }
  let revertValue = props.value !== undefined && props.value !== null ? props.value : (props.field.default ?? '');
  if (props.field.type === 'measurement' && typeof revertValue === 'number') {
    revertValue = convertLength(revertValue);
  }
  internalValue.value = revertValue;

  // Numeric types emit change with 'end' phase — SettingPanelControls routes
  // projector targets through the orbital session commit, others through normal update.
  if (['number', 'measurement', 'angle'].includes(props.field.type)) {
    emit('change', endValue, false, 'end');
  }
};

const handleEnterKey = () => {
  let valueToEmit = internalValue.value;
  if (!isValidValue(valueToEmit, props.field.type)) return;

  if (props.field.type === 'measurement' && typeof valueToEmit === 'number') {
    valueToEmit = toMeters(valueToEmit);
  }

  if (props.field.root) {
    emit('change', valueToEmit, true);
  } else {
    emit('change', valueToEmit);
  }
};
const saveValue = (value) => {
  // After blur, PrimeVue fires value-change which calls saveValue.
  // For projector target fields the orbit session has already committed
  // the constrained value in handleBlur → change with phase='end', so this late event
  // must be ignored to avoid overwriting with the raw (unconstrained) value.
  if (isProjectorTargetField.value && !isEditing.value) return;
  if (props.field.type === 'measurement' && typeof value === 'number') {
    value = toMeters(value);
  }
  handleChange(value);
};

const handleChange = (value) => {
  if (!isValidValue(value, props.field.type)) return;
  if (props.value === value) return;
  emit('change', value, !!props.field.root);
};

const overrideColorByGlobalMaterial = computed(() => {
  return (
    (props.module &&
      props.module?.type !== SETTING_PANEL_TABS.VIEWPORT_SETTINGS &&
      props.field.key === 'color' &&
      cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.VIEWPORT_SETTINGS)?.data['render.globalMaterialOverride']) ||
    false
  );
});

const isDisabled = computed(() => {
  return props.disabled || !isFieldDisabled.value || isLocked.value || false;
});

const isFieldDisabled = computed(() => {
  if (!props.field.disabled) return true;

  if (typeof props.field.disabled === 'function') {
    return props.field.disabled(props.module?.data || {});
  }

  return true;
});

const isVisible = computed(() => {
  if (!props.field.visible) return true;

  if (typeof props.field.visible === 'function') {
    return props.field.visible(props.module?.data || {});
  }

  return true;
});

const isLocked = computed(() => {
  if (!props.module?.data) return false;
  if (props.field?.key == 'lock') return false;
  return props.module.data?.lock || false;
});

const showContextMenu = (event) => {
  event.preventDefault();
  emit('contextmenu', { event, field: props.field });
};

const handleButtonClick = () => {
  if (props.field.onClick && typeof props.field.onClick === 'function') {
    props.field.onClick();
  }
};

// Get the unit label for measurement fields

const lengthUnitLabel = computed(() => getLengthUnitLabel());
const angleUnitLabel = computed(() => getAngleUnitLabel());

// Computed properties for measurement field step and precision based on unit
const measurementStep = computed(() => {
  if (props.field.type !== 'measurement') return props.field.step || 0.01;

  // Adjust step based on unit scale
  const baseStep = props.field.step || 0.01;
  const stepMultipliers = {
    millimeters: 1000,
    centimeters: 100,
    meters: 1,
    inches: 39.3701,
    feet: 3.28084,
  };

  return baseStep * (stepMultipliers[lengthUnit.value] || 1);
});

const measurementMaxFractionDigits = computed(() => {
  if (props.field.type !== 'measurement') return props.field.maxFractionDigits || 2;

  // Adjust precision based on unit
  const precisionMap = {
    millimeters: 0,
    centimeters: 1,
    meters: 3,
    inches: 2,
    feet: 3,
  };

  return props.field.maxFractionDigits || precisionMap[lengthUnit.value] || 2;
});
</script>
